import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { schemeAiPalette, Typography } from "@platform/design-system";
import { CELL_DIFFERENCE_BG } from "@platform/mpx-quotes-grid";

import type React from "react";

interface TriangleIconProps {
  variant: "top-left" | "top-right";
  color: string;
  size?: number;
}

const TriangleIcon: React.FC<TriangleIconProps> = ({
  variant,
  color,
  size = 7,
}) => (
  <Box
    sx={{
      width: 0,
      height: 0,
      borderStyle: "solid",
      borderWidth:
        variant === "top-left"
          ? `${size}px ${size}px 0 0`
          : `${size}px 0 0 ${size}px`,
      borderColor: `${color} transparent transparent transparent`,
    }}
  />
);

interface CircleBackgroundProps {
  bgColor?: string;
  children?: React.ReactNode;
}

const CircleBackground: React.FC<CircleBackgroundProps> = ({
  bgColor,
  children,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 18,
      height: 18,
      borderRadius: "50%",
      backgroundColor: bgColor ?? "grey.200",
      color: TEXT_SECONDARY,
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const TEXT_SECONDARY = "text.secondary";

interface AiPaletteColors {
  extracted?: string;
  comment?: string;
  reviewBackground?: string;
  extractionError?: string;
  edited?: string;
}

const defaultAiPalette: AiPaletteColors = {
  extracted: "#64B5F6",
  comment: "#FFB74D",
  reviewBackground: "#FFF8E1",
  extractionError: "#FFB74D",
  edited: "brandPrimary.main",
};

interface LegendItem {
  label: string;
  bgColor?: string;
  icon?: React.ReactNode;
}

const QuotesLegend = (): React.JSX.Element => {
  const theme = useTheme();
  // Scheme-aware, so the legend swatches match the cells they explain in dark
  // mode; `theme.palette.ai` alone is frozen to the light literals.
  const aiPalette =
    (schemeAiPalette(theme) as AiPaletteColors | undefined) ?? defaultAiPalette;

  const legendItems: LegendItem[] = [
    {
      label: "Broker Highlight",
      bgColor: "#E3F2FD",
    },
    {
      label: "Different Than Requested",
      bgColor: CELL_DIFFERENCE_BG,
    },
    {
      label: "Needs Review",
      bgColor: aiPalette.reviewBackground ?? defaultAiPalette.reviewBackground,
    },
    {
      label: "Comment",
      icon: (
        <TriangleIcon
          variant="top-right"
          color={aiPalette.comment ?? defaultAiPalette.comment!}
        />
      ),
    },
    {
      label: "AI Extracted",
      icon: (
        <TriangleIcon
          variant="top-left"
          color={aiPalette.extracted ?? defaultAiPalette.extracted!}
        />
      ),
    },
    {
      label: "Mapping Error",
      icon: (
        <WarningAmberOutlinedIcon
          sx={{
            color:
              aiPalette.extractionError ?? defaultAiPalette.extractionError,
            fontSize: 14,
          }}
        />
      ),
    },
    {
      label: "Edited",
      icon: (
        <EditOutlinedIcon
          sx={{
            color: aiPalette.edited ?? defaultAiPalette.edited,
            fontSize: 14,
          }}
        />
      ),
    },
    {
      label: "No Data Found",
      icon: (
        <Box
          component="span"
          sx={{
            color: TEXT_SECONDARY,
            fontSize: 8,
            fontWeight: 500,
            lineHeight: 1,
            letterSpacing: "0.4px",
          }}
        >
          {`[ ]`}
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        px: 1,
        py: 1,
        // `common.white` is a fixed value (doesn't flip); `background.paper` is
        // scheme-aware, so the legend bar follows the colour scheme.
        backgroundColor: "background.paper",
        borderTop: "1px solid",
        borderLeft: "1px solid",
        borderRight: "1px solid",
        borderColor: "divider",
        overflowX: "auto",
      }}
    >
      <Typography
        variant="body-medium"
        sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        color="textSecondary"
      >
        <strong>Legend:</strong>
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          minWidth: "max-content",
        }}
      >
        {legendItems.map(({ label, bgColor, icon }) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexShrink: 0,
            }}
          >
            <CircleBackground bgColor={bgColor}>{icon}</CircleBackground>
            <Typography
              variant="caption"
              sx={{ whiteSpace: "nowrap" }}
              color="textSecondary"
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default QuotesLegend;
